import { useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCw, CloudUpload } from "lucide-react";
import api from "../lib/api";
import { onQueueChange, queueCount, syncQueue } from "../lib/offlineQueue";
import { toast } from "sonner";

export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const refresh = async () => {
      try { setPending(await queueCount()); } catch (_) { /* noop */ }
    };
    refresh();
    const off = onQueueChange(refresh);
    return () => off();
  }, []);

  useEffect(() => {
    const goOnline = async () => {
      setOnline(true);
      toast.success("Connexion rétablie · synchronisation en cours…");
      await runSync();
    };
    const goOffline = () => {
      setOnline(false);
      toast.warning("Mode hors-ligne · vos modifications sont mises en file d'attente");
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    // Initial sync attempt if online and items pending
    if (navigator.onLine) runSync();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { synced, failed } = await syncQueue(api);
      if (synced > 0) toast.success(`${synced} action${synced > 1 ? "s" : ""} synchronisée${synced > 1 ? "s" : ""}`);
      if (failed > 0 && navigator.onLine) toast.error(`${failed} action${failed > 1 ? "s" : ""} en échec`);
    } finally {
      setSyncing(false);
    }
  };

  if (online && pending === 0) {
    // Show subtle "online" pill (no clutter)
    return (
      <span className="hidden sm:flex items-center gap-1 text-[10px] text-[#2E7D32] font-medium" title="En ligne" data-testid="offline-indicator-online">
        <Wifi className="w-3 h-3" /> En ligne
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs" data-testid="offline-indicator">
      {!online && (
        <span className="flex items-center gap-1 px-2 py-1 rounded bg-[#F57F17]/15 text-[#F57F17] border border-[#F57F17]/40 font-medium" data-testid="offline-badge">
          <WifiOff className="w-3.5 h-3.5" /> Hors-ligne
        </span>
      )}
      {pending > 0 && (
        <button
          onClick={runSync}
          disabled={syncing || !online}
          className="flex items-center gap-1 px-2 py-1 rounded bg-[#1976D2]/15 text-[#1976D2] border border-[#1976D2]/40 font-medium hover:bg-[#1976D2]/25 disabled:opacity-60"
          title={online ? "Synchroniser maintenant" : "Sera synchronisé au retour de la connexion"}
          data-testid="sync-badge"
        >
          {syncing
            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            : <CloudUpload className="w-3.5 h-3.5" />}
          {pending} en attente
        </button>
      )}
    </div>
  );
}
